// frontend/src/pages/CartPage.jsx
// Cart page with proper token economy integration
// Updated: Uses tokenEconomy utilities for accurate token calculations
// Fixed: Reads prices directly from cartItems with proper fallbacks
// FIX Mar 13 2026: Apply bulk discount to line items so cart matches checkout subtotal

import React, { useMemo } from 'react';
import { ArrowLeft, ShoppingCart as CartIcon, Trash2, Plus, Minus, Coins, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import BottomNav from '../components/common/BottomNav';
import { 
  calculatePurchaseTokens, 
  calculateCartTokens,
  formatTokens,
  formatTokenValue,
  getBulkBonusDisplay
} from '../utils/tokenEconomy';

// Bulk discount tiers — must match getCartTotals() in CartContext
const getBulkDiscount = (quantity) => {
  if (quantity >= 5) return 0.05; // 5% off
  if (quantity >= 3) return 0.03; // 3% off
  return 0;
};

const CartPage = () => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    updateQuantity, 
    removeFromCart, 
    clearCart 
  } = useCart();

  // Process cart items with prices, tokens, AND bulk discounts
  const cartWithTokens = useMemo(() => {
    return cartItems.map(item => {
      // Get price with multiple fallbacks
      const unitPrice = item.price || item.currentPrice || item.unitPrice || item.basePrice || 0;
      const quantity = item.quantity || 1;
      
      // Apply bulk discount (matches getCartTotals in CartContext)
      const discount = getBulkDiscount(quantity);
      const discountedUnitPrice = unitPrice * (1 - discount);
      const totalPrice = discountedUnitPrice * quantity;
      const originalTotal = unitPrice * quantity;
      const savingsAmount = originalTotal - totalPrice;
      
      // Token calculations
      const tokenCalc = calculatePurchaseTokens({ quantity });
      const bulkDisplay = getBulkBonusDisplay(quantity);
      
      return {
        ...item,
        unitPrice,
        discountedUnitPrice,
        discount,
        discountPercent: Math.round(discount * 100),
        quantity,
        totalPrice,
        originalTotal,
        savingsAmount,
        tokenCalc,
        bulkDisplay,
        calculatedTokens: tokenCalc.totalTokens,
        // Normalize property names
        title: item.name || item.title || 'Item',
        image: item.image || item.images?.[0]?.full || item.images?.[0]?.thumbnail || null,
        vendor: item.vendor || item.vendorName || 'Vendor'
      };
    });
  }, [cartItems]);

  // Calculate cart subtotal with bulk discounts applied (matches getCartTotals)
  const subtotal = useMemo(() => {
    return cartWithTokens.reduce((sum, item) => sum + item.totalPrice, 0);
  }, [cartWithTokens]);

  // Calculate total savings from bulk discounts
  const totalBulkSavings = useMemo(() => {
    return cartWithTokens.reduce((sum, item) => sum + item.savingsAmount, 0);
  }, [cartWithTokens]);

  // Original subtotal before any bulk discounts
  const originalSubtotal = useMemo(() => {
    return cartWithTokens.reduce((sum, item) => sum + item.originalTotal, 0);
  }, [cartWithTokens]);

  // Calculate total cart tokens using the proper system
  const cartTokenSummary = useMemo(() => {
    const totalQuantity = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    return calculatePurchaseTokens({ quantity: totalQuantity });
  }, [cartItems]);

  // Total item count
  const itemCount = cartItems.length;
  const totalUnits = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  const handleQuantityChange = (itemId, groupId, delta) => {
    const item = cartItems.find(i => i.id === itemId && i.groupId === groupId);
    if (item) {
      const newQuantity = (item.quantity || 1) + delta;
      updateQuantity(itemId, groupId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId, groupId) => {
    removeFromCart(itemId, groupId);
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    navigate('/home');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-4 py-4 flex items-center">
            <button 
              onClick={() => navigate(-1)}
              className="mr-3 p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-semibold">My Cart</h1>
          </div>
        </div>

        {/* Empty Cart */}
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <CartIcon size={64} className="text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6 text-center px-4">
            Add items from groups to start saving with collective purchasing!
          </p>
          <button
            onClick={handleContinueShopping}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Start Shopping
          </button>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(-1)}
              className="mr-3 p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-semibold">My Cart ({totalUnits} items)</h1>
          </div>
          <button
            onClick={clearCart}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Cart Items */}
      <div className="max-w-lg mx-auto p-4">
        <div className="space-y-4">
          {cartWithTokens.map((item, index) => (
            <div key={`${item.id}-${index}`} className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-start space-x-3">
                {item.image ? (
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="w-16 h-16 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <div className="text-3xl" style={{ display: item.image ? 'none' : 'block' }}>📦</div>
                <div className="flex-1">
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.vendor}</p>
                  <p className="text-sm text-gray-500">${item.unitPrice.toFixed(2)} each</p>
                  
                  {/* Bulk Hero Badge - using token economy system */}
                  {item.bulkDisplay.isBonus && (
                    <div className="mt-1">
                      <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full flex items-center gap-1 w-fit">
                        <Award size={12} />
                        {item.bulkDisplay.currentMessage}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveItem(item.id, item.groupId)}
                  className="p-2 hover:bg-gray-100 rounded-full text-red-600"
                >
                  <Trash2 size={18} />
                </button>
              </div>

              {/* Quantity and Price */}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center">
                  <button
                    onClick={() => handleQuantityChange(item.id, item.groupId, -1)}
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                    disabled={item.quantity <= 1}
                  >
                    <Minus size={16} />
                  </button>
                  <span className="mx-3 font-semibold text-lg">{item.quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(item.id, item.groupId, 1)}
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                    disabled={item.quantity >= 99}
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-lg">${item.totalPrice.toFixed(2)}</div>
                  {/* Show original price and savings when bulk discount applies */}
                  {item.discount > 0 && (
                    <div className="flex items-center gap-1.5 justify-end">
                      <span className="text-xs text-gray-400 line-through">${item.originalTotal.toFixed(2)}</span>
                      <span className="text-xs text-green-600 font-medium">-{item.discountPercent}% bulk</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Token Reward - FIXED: Using token economy calculations */}
              <div className="mt-3 bg-yellow-50 rounded-lg p-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-yellow-700">
                  <Coins size={16} />
                  <span className="text-sm font-medium">
                    Earn {formatTokens(item.calculatedTokens)} tokens
                  </span>
                </div>
                <span className="text-xs text-yellow-600">
                  ({formatTokenValue(item.calculatedTokens)})
                </span>
              </div>
              
              {/* Show bulk bonus breakdown if applicable */}
              {item.tokenCalc.bulkBonus > 0 && (
                <div className="mt-1 text-xs text-orange-600 text-right">
                  Includes +{formatTokens(item.tokenCalc.bulkBonus)} bulk bonus!
                </div>
              )}
              
              {/* Show next bonus hint */}
              {!item.bulkDisplay.isBonus && item.bulkDisplay.nextBonusAt && (
                <p className="mt-1 text-xs text-gray-500 text-center">
                  {item.bulkDisplay.nextMessage}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="mt-6 bg-white rounded-lg shadow-sm p-4">
          <h3 className="font-semibold mb-3">Order Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal ({totalUnits} items)</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>

            {/* Bulk Savings Line - only show when there are savings */}
            {totalBulkSavings > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span className="flex items-center gap-1">
                  <Award size={14} />
                  Bulk discount savings
                </span>
                <span>-${totalBulkSavings.toFixed(2)} included</span>
              </div>
            )}
            
            {/* Token Summary */}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-sm text-yellow-700">
                <span className="flex items-center gap-1">
                  <Coins size={14} />
                  Base Tokens
                </span>
                <span>{formatTokens(cartTokenSummary.baseTokens)}</span>
              </div>
              {cartTokenSummary.bulkBonus > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span className="flex items-center gap-1">
                    <Award size={14} />
                    Bulk Bonuses
                  </span>
                  <span>+{formatTokens(cartTokenSummary.bulkBonus)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold text-yellow-700 mt-1 pt-1 border-t border-yellow-200">
                <span>Total Tokens to Earn</span>
                <span>{formatTokens(cartTokenSummary.totalTokens)} ({formatTokenValue(cartTokenSummary.totalTokens)})</span>
              </div>
            </div>

            {/* Subtotal before fees */}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between font-semibold text-lg">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {/* Fees/tax disclosure */}
              <p className="text-xs text-gray-500 mt-1">
                Platform fee (7.5%), logistics fee (7.5%), and CA sales tax (7.25%) calculated at checkout.
              </p>
            </div>
          </div>
        </div>

        {/* Token Value Reminder */}
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">💰</span>
            <div>
              <p className="text-sm font-medium text-yellow-800">
                {formatTokens(cartTokenSummary.totalTokens)} tokens = {formatTokenValue(cartTokenSummary.totalTokens)} in future savings!
              </p>
              <p className="text-xs text-yellow-600">
                1 token = $0.10 • Redeem at checkout on future orders
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-3">
          <button
            onClick={handleCheckout}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
          >
            Proceed to Checkout
            <span className="text-sm opacity-80">• Earn {formatTokens(cartTokenSummary.totalTokens)} tokens</span>
          </button>
          <button
            onClick={handleContinueShopping}
            className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50"
          >
            Continue Shopping
          </button>
        </div>

        {/* Info Banner */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-1 flex items-center gap-2">
            <Award size={16} />
            Bulk Hero Benefits
          </h4>
          <p className="text-sm text-blue-700">
            Order 3+ units of any item to get Bulk Hero status with +25% bonus tokens and 3% off!
            Order 5+ units for +50% bonus tokens and 5% off!
          </p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default CartPage;